// Copyright (c) Microsoft. All rights reserved.

import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
// import rehypeHighlight from 'rehype-highlight';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw'; // 추가
import remarkDirective from "remark-directive";
import { visit } from "unist-util-visit";
import { Root } from 'mdast';
import mermaid from 'mermaid';
import { Canvg } from 'canvg';
import { Element as HastElement } from 'hast';

import { Button } from '../../../components/Button';
import { checkMobileKate, checkMoileClickDenyUrl } from '../../../components/utils/common';
import { IMCPResponse } from '../../../libs/models/app/mcp/Tool';
import type { TextDirective, LeafDirective, ContainerDirective } from 'mdast-util-directive';
import { Switch, Tooltip } from '../../../components/base';
import { ChatHistoryToolResult } from './ChatHistoryToolResult';
import { Card } from '../../../components/base/Card';
import classNames from 'classnames';

interface Node {
  type: string;
  tagName?: string;
  properties: {
    className: Node[];
    raw: string | null;
  };
  value: string;
  children?: Node[];
}

type DirectiveNode = TextDirective | LeafDirective | ContainerDirective;

interface ChatHistoryTextContentProps {
  copyToClipboard: (text: string) => Promise<void>;
  text: string;
  isTyping: boolean;
}

type CustomComponents = Components & {
  tool_result?: React.FC<{ toolName: string, node: Node }>;
};

mermaid.initialize({ startOnLoad: false, securityLevel: 'loose', flowchart: { htmlLabels: false }, theme: 'default', fontFamily: 'Noto Sans KR', htmlLabels: false });
const remarkCustomDirective = () => {
  return (tree: Root) => {
    visit(tree, "containerDirective", (node: DirectiveNode) => {
      if (node.name === "tool_result") {
        const data = node.data ?? (node.data = {});
        // 이 시점에서 React로 넘길 HTML 태그 지정
        data.hName = node.name;
        data.hProperties = node.attributes ?? {};
      }
    });
  };
}

function extractRawText(mdNode: Node): string {
  if (!mdNode) return '';
  if (mdNode.type === 'text') return mdNode.value || '';
  if (mdNode.children) return mdNode.children.map(extractRawText).join('');
  return '';
}


export const checkDownloadUrl = (url?: string) => {
  if (url && url.startsWith('download?s=')) {
    return { isDownloadUrl: true, url: process.env.REACT_APP_BACKEND_URI + '/' + url };
  } else return { isDownloadUrl: false, url: null };
};
const CodeBlock = ({ node, children, className, copyToClipboard, isTyping, ...props }: { node: HastElement | undefined, isTyping: boolean, children: React.ReactNode, copyToClipboard: (text: string) => Promise<void>, className: string | undefined }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [showChart, setShowChart] = React.useState(false);
  const [isChart, setIsChart] = React.useState(false);
  const [code, setCode] = React.useState("");
  const match = /language-(\w+)/.exec(className ?? '');

  React.useEffect(() => {
    const match = /language-(\w+)/.exec(className ?? '');
    if (match && match[1] == 'mermaid' && !isTyping) {

      const checkMermaid = async () => {
        let code = String(children).replace(/\[(?=[^\]]*[()])(.*?)\]/g, '["$1"]').trim();
        if (code.startsWith(",")) {
          code = code.substring(1);
        }
        if (code.endsWith(",")) {
          code = code.substring(0, code.length - 1);
        }
        const result = await mermaid.parse(code, { suppressErrors: true });
        setIsChart(result ? true : false);
        setCode(code);
      };

      void checkMermaid();
    }
  }, [isTyping])


  // 추출 함수 정의
  const extractCodeText = (node: Node): string => {
    let codeText = '';
    if (node.children) {
      for (const child of node.children) {
        if (child.children) {
          codeText += child.children[0].value;
        } else {
          codeText += child.value;
        }
      }
    }
    return codeText;
  };

  const handleCopyClick = () => {
    if (node && node.tagName === 'code') {
      const rawText = extractCodeText(node as unknown as Node);
      void copyToClipboard(rawText);
    }
  };

  const onShowChart = () => {
    setShowChart(!showChart);
  }


  const svgToPng = async () => {
    const svg = ref.current?.querySelector("svg");
    console.log(svg, ref.current);
    if (!svg) return null;

    const origWidth = svg.width.baseVal.value ?? 800;
    const origHeight = svg.height.baseVal.value ?? 600;

    const canvas = document.createElement("canvas");
    canvas.width = origWidth * 3;
    canvas.height = origHeight * 3;
    const ctx = canvas.getContext("2d");
    console.log(canvas);
    if (!ctx) return null;

    ctx.scale(3, 3);
    const v = Canvg.fromString(ctx, svg.outerHTML);
    await v.render();

    return canvas;
  };

  const downloadPng = async () => {
    const canvas = await svgToPng();
    if (!canvas) return;
    canvas.toBlob((pngBlob) => {
      if (pngBlob) {
        const downloadLink = document.createElement("a");
        downloadLink.href = URL.createObjectURL(pngBlob);
        downloadLink.download = "diagram.png";
        downloadLink.click();
        URL.revokeObjectURL(downloadLink.href);
      }
    }, "image/png");
  }


  return (
    className ? (
      <div className={classNames("code-block", { mermaid: showChart })}>
        <div className={"title"}>
          <span>{`${match ? match[1] : ''} `}</span>
          <div className='menu'>
            {!showChart ? //<Tooltip message={"복사"} variant='inverse'>
              <Button
                className="button"
                icon="copy"
                onClick={() => {
                  handleCopyClick();
                }}
              />
              //</Tooltip> 
              :
              <Tooltip message={"이미지 다운로드"} variant='inverse'>
                <Button
                  textBlind
                  icon="download"
                  className="button"
                  onClick={() => {
                    downloadPng().catch((e) => { console.log(e) });
                  }}
                >
                  이미지 다운로드
                </Button>
              </Tooltip>
            }
            {isChart &&
              <Tooltip message={showChart ? "코드 보기" : "이미지 보기"} variant='inverse'>
                <div>
                  <Switch checked={showChart} onChange={() => { onShowChart() }} />
                </div>
              </Tooltip>
            }
          </div>
        </div>
        <div className="code">
          <code className={`${match ? match[1] : ''} `} {...props}>
            {/* {children} */}
            {showChart ? <MermaidBlock ref={ref} code={code} mounted={isChart} /> : children}
          </code>
        </div>
      </div>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    )
  )
}

interface MermaidBlockProps {
  code: string,
  mounted: boolean
}
export const MermaidBlock = React.forwardRef<HTMLDivElement, MermaidBlockProps>(({ code, mounted }, ref) => {

  React.useEffect(() => {
    const el = (ref as React.RefObject<HTMLDivElement>).current;
    if (el && mounted) {
      void mermaid.init(undefined, el);
      if (code.startsWith('graph LR') || code.startsWith('flowchart LR')) {
        el.classList.add('mermaid-lr');
      }
      if (code.startsWith('graph TD') || code.startsWith('flowchart TD')) {
        el.classList.add('mermaid-td');
      }
    }
  }, [ref, mounted, code]);

  return (
    <div className='mermaid-block'>
      <Card
        variant='vertical'
        media={
          <div ref={ref} className='mermaid'>
            {code}
          </div>
        }
      >
      </Card>
    </div>
  )
}
)
MermaidBlock.displayName = "MermaidBlock";

export const ChatHistoryTextContent: React.FC<ChatHistoryTextContentProps> = ({ copyToClipboard, text, isTyping }) => {

  return (
    <>
      <div className="msg-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks, remarkDirective, remarkCustomDirective]}
          rehypePlugins={[rehypeRaw]} // 추가
          // rehypePlugins={[rehypeHighlight]}
          components={{
            a: ({ children, ...props }) => {
              if (props.href?.includes('http')) {
                props.target = '_blank';
                props.rel = 'noopener noreferrer';

                const regex = /\[\d+\]/g;
                const checkRefer = children?.toString().match(regex);
                if (checkRefer) {
                  props.className = 'msg-caution ds-link';
                  children = children?.toString().replace('[', '').replace(']', '');
                }

                if (checkMobileKate() && checkMoileClickDenyUrl(props.href)) {
                  props.className = `no-click`;
                }
              }
              const { isDownloadUrl, url } = checkDownloadUrl(props.href);
              if (isDownloadUrl && url) {
                props.target = '_blank';
                props.rel = 'noopener noreferrer';
                props.href = url;
                props.className = 'msg-caution ds-link no-click';
                children = children?.toString().replace('[', '').replace(']', '');
              }
              return <a {...props}>{children}</a>;
            },
            // 'del' 태그를 span 태그로 대체하여 취소선을 제거
            del: ({ ...props }) => <span>{props.children}</span>,
            table: ({ ...props }) => {
              return (
                <div className="msg-table">
                  <table>{props.children}</table>
                </div>
              );
            },
            code({ node, children, className, ...props }) {

              return <CodeBlock node={node} className={className} copyToClipboard={copyToClipboard} isTyping={isTyping} {...props}>{children}</CodeBlock>
            },
            tool_result: ({ ...props }) => {
              const raw = extractRawText(props.node);
              if (raw) {
                try {
                  const fixed = raw.replace(/\\(?![nrtbfn"\\/u])/g, '\\\\');
                  const result = JSON.parse(fixed) as IMCPResponse;
                  // const result = JSON.parse(raw) as IMCPResponse;
                  return <ChatHistoryToolResult toolName={props.toolName} result={result} />
                } catch {
                  return <></>
                }
              }
              return <></>
            },
            'skill-agent': ({ ...props }) => {
              const name = extractRawText(props.node as Node);
              if (!name) return <></>;
              return <span className="skill-chip">/{name}</span>;
            },
          } as CustomComponents}

        >
          {/* {item.message.replace(/\\n/g, '<br>')} */}
          {text.replace(/"[^"]*"/g, (match) => match.replace(/\:/g, '__COLON__'))
            .replace(/:::/g, ':::').replace(/(?<!\))(?<!-)(?<!\:)(?<!\s):(?!\:)(?!-)(?!\s)(?!\\)/g, '\\\:')
            .replace(/:::/g, ':::').replace(/\~/g, '\\\~')
            .replace(/__COLON__/g, '\:')}

          {/* {text} */}
          {/* {item.message} */}
        </ReactMarkdown>
        {/* {errorMessage && <Error message={errorMessage}></Error>} */}
      </div>
      {/* {item.references && item.references.length > 0 && (
        <article className="msg-subContent">
          <strong className="msg-title">이런 자료를 참고했어요.</strong>
          <div className="msg-reference">
            {item.references.map((data, index) => (
              <div className="item" key={index}>
                <Link
                  url={checkDownloadUrl(data.uri).url ?? data.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-click"
                >
                  <strong className="tit">
                    <i>{data.index}</i> {data.title}
                  </strong>
                  {data.subTitle && <span className="desc">{data.subTitle}</span>}
                </Link>
              </div>
            ))}
          </div>
        </article>
      )} */}
    </>
  );
};
